import React, {useEffect, useState} from "react";
import {AutoComplete, Input, message, Modal, Segmented, Space, Typography} from "antd";
import epicEndpoints from "../resources/EpicEndpoints.json";
import cernerEndpoints from "../resources/cerner_millennium_patient_r4_endpoints.json";
import {startFhirAuth} from "./AuthHelper";

const { Title } = Typography;

export interface FhirProviderModalProps {
    visible: boolean;
    executionId?: string;
    providerStep?: number;
    tabName?: string;
    onClose: () => void;
}

export function FhirProviderModal({ visible, onClose, tabName, executionId, providerStep }: FhirProviderModalProps) {
    const [step, setStep] = useState<number>(providerStep ?? 0);
    const [provider, setProvider] = useState<"epic" | "charm" | "athena" | "cerner">("epic");

    const [allEndpoints, setAllEndpoints] = useState<any[]>([]);
    const [endpoints, setEndpoints] = useState<any[]>([]);
    const [selected, setSelected] = useState<string>();

    useEffect(() => {
        if (!visible) return;
        fetchEndpoints(provider);
        setStep(providerStep ?? 0);
        setSelected(undefined);
    }, [visible, provider]);

    const fetchEndpoints = async (prov: string) => {
        if (!prov) return;

        if (prov === "epic") {
            const entries = epicEndpoints.entry.sort((a, b) =>
                a.resource.name.localeCompare(b.resource.name, undefined, { sensitivity: "base" })
            ).map((e: any) => {
                const name = e.resource.contained?.[0]?.name || e.resource.name;
                return { label: name, value: name, url: e.resource.address };
            });
            setAllEndpoints(entries);
            setEndpoints(entries);
            return;
        }

        if (prov === "cerner") {
            const orgs = cernerEndpoints.entry.filter(e => e.resource.resourceType === "Organization");
            const endpoints = cernerEndpoints.entry.filter(e => e.resource.resourceType === "Endpoint");

            const orgEntries = orgs.map(orgEntry => {
                const org = orgEntry.resource;
                const endpointRef = org.endpoint?.[0]?.reference;
                const endpointId = endpointRef?.split("/")[1];
                const endpoint = endpoints.find(e => e.resource.id === endpointId);
                const name = org.name;
                const url = endpoint?.resource?.address;
                return { label: name, value: name, url };
            }).filter(e => !!e.url); // remove nulls

            orgEntries.sort((a, b) =>
                (a.label ?? "").localeCompare(b.label ?? "", undefined, { sensitivity: "base" })
            );
            setAllEndpoints(orgEntries);
            setEndpoints(orgEntries);
        }
    };

    const goNext = async () => {
        if (step === 0) {
            if (!provider) return message.error("Choose a provider first.");
            if (provider === "epic" || provider === "cerner") {
                setStep(1);
            } else {
                onClose();
                await startFhirAuth(provider).catch((e) => message.error(e.message));
            }
            return;
        }

        if (!selected) return message.error("Select an organisation.");
        try {
            onClose();
            const endpoint = allEndpoints.find(end => end.label === selected);
            await startFhirAuth(provider, {
                endPoint: endpoint.url,
                tabName,
                executionId,
            });
        } catch (e: any) {
            message.error(e.message);
        }
    };

    return (
        <Modal
            open={visible}
            onCancel={onClose}
            onOk={goNext}
            okText={step === 0 ? "Next" : "Connect"}
            width={480}
            destroyOnClose
        >
            {step === 0 && (
                <>
                    <Title level={4}>Choose FHIR Provider</Title>
                    <Segmented
                        block
                        size="large"
                        value={provider}
                        onChange={(val) => setProvider(val as any)}
                        options={[
                            {
                                label: (
                                    <Space>
                                        <img src="/epiclogo.png" alt="Epic" style={{ width: 16 }} />
                                        Epic
                                    </Space>
                                ),
                                value: "epic",
                            },
                            {
                                label: (
                                    <Space>
                                        <img src="/charm2.png" alt="Charm" style={{ width: 16 }} />
                                        Charm
                                    </Space>
                                ),
                                value: "charm",
                            },
                            {
                                label: (
                                    <Space>
                                        <img src="/athena.png" alt="Athena" style={{ width: 16 }} />
                                        Athena
                                    </Space>
                                ),
                                value: "athena",
                            },
                            {
                                label: (
                                    <Space>
                                        <img src="/cernerlogo.png" alt="Cerner" style={{ width: 16 }} />
                                        Cerner
                                    </Space>
                                ),
                                value: "cerner",
                            },
                        ]}
                    />
                </>
            )}

            {step === 1 && (provider === "epic" || provider === "cerner") && (
                <>
                    <Title level={4}>
                        Select{" "}
                        <img
                            src={provider === "epic" ? "https://fhir.epic.com/Content/images/favicon.ico" : "/cernerlogo.png"}
                            alt={provider}
                            style={{ width: 16 }}
                        />{" "}
                        {provider.charAt(0).toUpperCase() + provider.slice(1)} organisation:
                    </Title>
                    <AutoComplete
                        style={{ width: "100%" }}
                        options={endpoints}
                        autoFocus={true}
                        onSearch={(t) =>
                            setEndpoints(allEndpoints.filter((e) =>
                                e.label?.toLowerCase().includes(t?.toLowerCase())
                            ))
                        }
                        onSelect={(v) => setSelected(v)}
                        placeholder="Type to search organisation…"
                    >
                        <Input />
                    </AutoComplete>
                </>
            )}
        </Modal>
    );
}
